import React, { useState, useEffect, useCallback, useContext } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import BlockIcon from "@mui/icons-material/Block";
import { toast } from "react-toastify";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Cobrança Pix (QR dinâmico) de uma OS: gera, envia no chat do atendimento e
// acompanha a confirmação automática. Backend: /service-orders/:id/pix.

const STATUS = {
  pending: { label: "Aguardando pagamento", color: "#ef6c00", bg: "#fff3e0" },
  paid: { label: "Pago", color: "#2e7d32", bg: "#e8f5e9" },
  expired: { label: "Expirado", color: "#757575", bg: "#eeeeee" },
  cancelled: { label: "Cancelado", color: "#757575", bg: "#eeeeee" },
  error: { label: "Erro", color: "#c62828", bg: "#ffebee" },
};

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const when = (d) => (d ? new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

const PixChargeDialog = ({ open, order, onClose }) => {
  const { user } = useContext(AuthContext);
  const canCancel = ["admin", "supervisor"].includes(user?.profile);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/service-orders/${order.id}/pix`);
      setSummary(data);
      setAmount(data.outstanding > 0 ? data.outstanding.toFixed(2).replace(".", ",") : "");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => {
    if (open) load();
    else setSummary(null);
  }, [open, load]);

  // Confirmação chega pelo poller do backend → socket "pixCharge".
  useEffect(() => {
    if (!open || !order?.id) return undefined;
    const socket = openSocket();
    socket.on("pixCharge", (data) => {
      if (data?.charge?.serviceOrderId !== order.id) return;
      if (data.action === "update" && data.charge.status === "paid") {
        toast.success(`Pix de ${brl(data.charge.paidAmount || data.charge.amount)} recebido — ${order.numero}`);
      }
      load();
    });
    return () => {
      socket.off("pixCharge");
      socket.disconnect();
    };
  }, [open, order?.id, order?.numero, load]);

  const parsedAmount = Number(String(amount).replace(/\./g, "").replace(",", "."));
  const outstanding = summary?.outstanding || 0;
  const amountInvalid = !(parsedAmount > 0) || parsedAmount > outstanding + 0.001;
  const noTicket = !order?.ticketId;

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post(`/service-orders/${order.id}/pix`, { amount: parsedAmount });
      toast.success("Cobrança Pix enviada no chat");
      await load();
    } catch (err) {
      toastError(err);
      load();
    } finally {
      setCreating(false);
    }
  };

  const handleResend = async (c) => {
    setBusyId(c.id);
    try {
      await api.post(`/service-orders/pix/${c.id}/resend`);
      toast.success("Cobrança reenviada no chat");
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (c) => {
    if (!window.confirm(`Cancelar a cobrança de ${brl(c.amount)}? O QR enviado deixa de funcionar.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/service-orders/pix/${c.id}/cancel`);
      toast.info("Cobrança cancelada");
      await load();
    } catch (err) {
      toastError(err);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const handleCopy = async (c) => {
    try {
      await navigator.clipboard.writeText(c.pixCopiaECola || "");
      toast.success("Pix copia e cola copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <QrCode2Icon style={{ color: "#00897b" }} />
        Cobrar via Pix — OS {order?.numero}
      </DialogTitle>
      <DialogContent dividers>
        {loading && !summary ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><CircularProgress size={28} /></div>
        ) : summary && (
          <>
            {!summary.enabled && (
              <Alert severity="info" style={{ marginBottom: 16 }}>
                A cobrança Pix ainda não está ativa: falta configurar as credenciais da API Pix do Sicoob.
              </Alert>
            )}
            {summary.enabled && noTicket && (
              <Alert severity="warning" style={{ marginBottom: 16 }}>
                Esta OS não está vinculada a um atendimento — não há para onde enviar o QR.
              </Alert>
            )}

            <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <div><Typography variant="caption" color="textSecondary">Total da OS</Typography><Typography style={{ fontWeight: 600 }}>{brl(summary.netTotal)}</Typography></div>
              <div><Typography variant="caption" color="textSecondary">Recebido via Pix</Typography><Typography style={{ fontWeight: 600, color: "#2e7d32" }}>{brl(summary.paid)}</Typography></div>
              <div><Typography variant="caption" color="textSecondary">Saldo a cobrar</Typography><Typography style={{ fontWeight: 600 }}>{brl(outstanding)}</Typography></div>
            </div>

            {outstanding > 0 && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 8 }}>
                <TextField
                  label="Valor desta cobrança"
                  size="small"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
                  error={Boolean(amount) && amountInvalid}
                  helperText={Boolean(amount) && amountInvalid ? `Entre R$ 0,01 e ${brl(outstanding)}` : " "}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  style={{ width: 200 }}
                />
                <Button size="small" onClick={() => setAmount(outstanding.toFixed(2).replace(".", ","))} style={{ marginTop: 4, textTransform: "none" }}>
                  Saldo todo
                </Button>
                <Button size="small" onClick={() => setAmount((Math.round(outstanding * 50) / 100).toFixed(2).replace(".", ","))} style={{ marginTop: 4, textTransform: "none" }}>
                  Metade (sinal)
                </Button>
              </div>
            )}

            {summary.charges.length > 0 && (
              <>
                <Typography variant="subtitle2" style={{ margin: "8px 0" }}>Cobranças desta OS</Typography>
                {summary.charges.map((c) => {
                  const st = STATUS[c.status] || STATUS.error;
                  return (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid #eee" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{brl(c.status === "paid" ? c.paidAmount || c.amount : c.amount)}</div>
                        <div style={{ fontSize: 12, color: "#757575" }}>
                          {c.status === "paid" ? `pago em ${when(c.paidAt)}` : `gerada em ${when(c.createdAt)}${c.status === "pending" && c.expiresAt ? ` · vence ${when(c.expiresAt)}` : ""}`}
                        </div>
                        {c.status === "paid" && (
                          <div style={{ fontSize: 12, color: c.blingSettledAt ? "#2e7d32" : c.blingError ? "#c62828" : "#757575" }}>
                            {c.blingSettledAt
                              ? "✓ Lançado e baixado no Bling"
                              : c.blingError
                                ? (c.blingAttempts >= 8 ? `Bling: falhou — lançar manualmente (${c.blingError})` : `Bling: tentando de novo (${c.blingError})`)
                                : "Bling: lançando…"}
                          </div>
                        )}
                      </div>
                      <Chip size="small" label={st.label} style={{ color: st.color, backgroundColor: st.bg, fontWeight: 600 }} />
                      {c.status === "pending" && (
                        <>
                          <Tooltip title="Copiar Pix copia e cola">
                            <IconButton size="small" onClick={() => handleCopy(c)}><ContentCopyIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Reenviar no chat">
                            <span>
                              <IconButton size="small" onClick={() => handleResend(c)} disabled={busyId === c.id || noTicket}>
                                {busyId === c.id ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canCancel && (
                            <Tooltip title="Cancelar cobrança">
                              <span>
                                <IconButton size="small" onClick={() => handleCancel(c)} disabled={busyId === c.id}>
                                  <BlockIcon fontSize="small" style={{ color: "#c62828" }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ textTransform: "none" }}>Fechar</Button>
        {summary && outstanding > 0 && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!summary.enabled || noTicket || amountInvalid || creating}
            startIcon={creating ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <QrCode2Icon />}
            style={{ textTransform: "none", backgroundColor: !summary.enabled || noTicket || amountInvalid || creating ? undefined : "#00897b", color: !summary.enabled || noTicket || amountInvalid || creating ? undefined : "#fff" }}
          >
            Gerar e enviar no chat
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PixChargeDialog;
